import { useEffect, useLayoutEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Link as RouterLink } from 'react-router-dom';
import { formatHomeDate } from '../../homeUtils';
import type { Announcement } from '@internal/plugin-management-common';
import type { Theme } from '@material-ui/core/styles';
import type { PaletteColor } from '@material-ui/core/styles/createPalette';

interface HomeAnnouncementTheme extends Theme {
  palette: Theme['palette'] &
    Record<
      'info' | 'success' | 'warning' | 'error',
      PaletteColor & { announcementBackground: string }
    >;
}

const TYPE_LABELS: Record<string, string> = {
  info: 'NUEVO',
  success: 'ACTUALIZACIÓN',
  warning: 'AVISO',
  critical: 'URGENTE',
};

interface HomeAnnouncementCardProps {
  announcement: Announcement;
  index: number;
  length: number;
  onPrevious: () => void;
  onNext: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export const HomeAnnouncementCard = ({
  announcement,
  index,
  length,
  onPrevious,
  onNext,
  onMouseEnter,
  onMouseLeave,
}: HomeAnnouncementCardProps) => {
  const classes = useStyles();
  const cardRef = useRef<HTMLDivElement>(null);
  const previousHeightRef = useRef<number>();
  const animationRef = useRef<Animation | null>(null);
  const { type, date, title, content, link } = announcement;
  const severity = type.toLowerCase();
  const severityClasses: Record<string, string> = {
    info: classes.info,
    success: classes.success,
    warning: classes.warning,
    error: classes.critical,
    critical: classes.critical,
  };
  const chipClasses: Record<string, string> = {
    info: classes.infoChip,
    success: classes.successChip,
    warning: classes.warningChip,
    error: classes.criticalChip,
    critical: classes.criticalChip,
  };

  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const previousHeight =
      animationRef.current?.playState === 'running'
        ? card.getBoundingClientRect().height
        : previousHeightRef.current;
    animationRef.current?.cancel();
    const height = card.getBoundingClientRect().height;
    previousHeightRef.current = height;

    if (
      previousHeight === undefined ||
      previousHeight === height ||
      typeof card.animate !== 'function' ||
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    animationRef.current = card.animate(
      [{ height: `${previousHeight}px` }, { height: `${height}px` }],
      { duration: 280, easing: 'ease' },
    );
  }, [
    announcement.content,
    announcement.date,
    announcement.link,
    announcement.title,
    announcement.type,
    index,
    length,
  ]);

  useEffect(() => () => animationRef.current?.cancel(), []);

  return (
    <Card
      ref={cardRef}
      className={`${classes.card} ${severityClasses[severity] ?? classes.info}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <CardContent className={classes.content}>
        <Box className={classes.header}>
          <Chip
            size="small"
            label={TYPE_LABELS[severity] ?? type.toUpperCase()}
            className={`${classes.chip} ${
              chipClasses[severity] ?? classes.infoChip
            }`}
          />
          <Typography className={classes.title}>{title}</Typography>
          <Typography className={classes.date}>
            {formatHomeDate(date, true)}
          </Typography>
          {length > 1 && (
            <Box className={classes.controls}>
              <Tooltip title="Anterior">
                <IconButton
                  size="small"
                  onClick={onPrevious}
                  aria-label="Anuncio anterior"
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography className={classes.counter}>
                {index + 1} / {length}
              </Typography>
              <Tooltip title="Siguiente">
                <IconButton
                  size="small"
                  onClick={onNext}
                  aria-label="Anuncio siguiente"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
        <Typography className={classes.description}>{content}</Typography>
        {link &&
          (/^https?:\/\//i.test(link) ? (
            <Button
              component="a"
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.cta}
            >
              Ver anuncio →
            </Button>
          ) : (
            <Button component={RouterLink} to={link} className={classes.cta}>
              Ver anuncio →
            </Button>
          ))}
      </CardContent>
    </Card>
  );
};

const useStyles = makeStyles<HomeAnnouncementTheme>(theme => ({
  card: {
    display: 'block',
    height: 'auto',
    border: 'none',
    borderLeft: '4px solid',
    borderRadius: 0,
    boxShadow: 'none',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  info: {
    borderLeftColor: theme.palette.info.main,
    backgroundColor: theme.palette.info.announcementBackground,
  },
  success: {
    borderLeftColor: theme.palette.success.main,
    backgroundColor: theme.palette.success.announcementBackground,
  },
  warning: {
    borderLeftColor: theme.palette.warning.main,
    backgroundColor: theme.palette.warning.announcementBackground,
  },
  critical: {
    borderLeftColor: theme.palette.error.main,
    backgroundColor: theme.palette.error.announcementBackground,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
    gap: theme.spacing(0.75),
    padding: `${theme.spacing(1.5)}px ${theme.spacing(2)}px !important`,
    '&:last-child': { paddingBottom: `${theme.spacing(1.75)}px !important` },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
    '@media (max-width: 600px)': {
      alignItems: 'flex-start',
      flexWrap: 'wrap',
    },
  },
  chip: {
    margin: 0,
    letterSpacing: '0.04em',
  },
  infoChip: {
    color: theme.palette.info.dark,
    backgroundColor: theme.palette.action.selected,
  },
  successChip: {
    color: theme.palette.success.dark,
    backgroundColor: theme.palette.action.selected,
  },
  warningChip: {
    color: theme.palette.warning.dark,
    backgroundColor: theme.palette.action.selected,
  },
  criticalChip: {
    color: theme.palette.error.dark,
    backgroundColor: theme.palette.action.selected,
  },
  date: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    flexShrink: 0,
    borderLeft: `1px solid ${theme.palette.border}`,
    '& .MuiIconButton-root': { padding: theme.spacing(0.5) },
  },
  counter: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 600,
    minWidth: 34,
    textAlign: 'center',
    fontVariantNumeric: 'tabular-nums',
  },
  title: {
    color: theme.palette.text.primary,
    fontSize: '1.12rem',
    fontWeight: 700,
    lineHeight: 1.3,
    flex: '1 1 100%',
    minWidth: 0,
    overflowWrap: 'anywhere',
    marginLeft: theme.spacing(0.75),
  },
  description: {
    color: theme.palette.text.secondary,
    fontSize: '1.05rem',
    lineHeight: 1.5,
    whiteSpace: 'pre-line',
    overflowWrap: 'anywhere',
  },
  cta: {
    alignSelf: 'flex-start',
    marginTop: theme.spacing(0.5),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    fontSize: '0.8rem',
    textTransform: 'none',
    borderRadius: 4,
    padding: theme.spacing(0.625, 1.5),
    '&:hover': { backgroundColor: theme.palette.primary.dark },
  },
}));
